import {Component, ElementRef, inject, signal, ViewChild} from '@angular/core';
import {Field, form, FormField, validateStandardSchema} from '@angular/forms/signals';
import {z} from 'zod';
import {InputText} from 'primeng/inputtext';
import {ZodAutoValidateDirective} from '../../shared/decorators/auto-validate/auto-validate';
import {postResource} from '../../shared/helpers/post-resource';
import {AppBaseComponent} from '../../shared/components/app-base-component/app-base-component';
import {LOGIN} from '../../event-disctionary';
import {register} from 'node:module';
import {RouterLink} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';

declare var window: any;
declare const google: any;
const userSchema = z.object({
  email: z.email(),
  password: z.string(),
  confirm_password: z.string().optional(),
}).refine((data) => {
  if (data.confirm_password) {
    return data.password === data.confirm_password;
  }
  return true;
}, {
  message: "Passwords don't match",
  path: ["confirm_password"]
});

@Component({
  selector: 'app-login',
  imports: [
    InputText,
    ZodAutoValidateDirective,
    FormField,
    RouterLink,
    FormsModule,
  ],
  templateUrl: './login.html',
  styleUrl: './login.css',
})

export class Login extends AppBaseComponent {
  returnUrl = signal(null);
  showPassword = signal(false);
  showRegister = signal(false);
  loginModel = signal({email: '', password: '', confirm_password: ''})
  loginForm = form(this.loginModel, (schemaPath) => {
    validateStandardSchema(schemaPath, userSchema)
  });
  request = postResource('user/login', this.loginModel);
  loginError = signal(false);
  resetStep = signal<0 | 1 | 2 | 3>(0);
  resetEmail = signal('');
  resetOtp = signal('');
  resetToken = signal('');
  newPassword = signal('');
  confirmNewPassword = signal('');
  resetLoading = signal(false);
  resetError = signal('');

  onSubmit(e: Event) {
    e.preventDefault();
    this.loginError.set(false);
    this.http.post('user/login', this.loginModel()).subscribe({
      next: (res: any) => {
        this.authService.saveUser(res);
        this.eventBus.cast(LOGIN, null);
        this.openTargetPage();
      },
      error: (err: HttpErrorResponse) => {
        if (err.status == 403) this.loginError.set(true);
      }
    })

  }

  openTargetPage() {
    if (this.returnUrl()) {
      this.router.navigate([this.returnUrl()]).then();
    } else {
      this.router.navigate(['/suggestions']).then();
    }
  }

  constructor() {
    super();
    if (this.isBrowser()) {
      this.activatedRoute.queryParams.subscribe((res: any) => {
        if (res['code']) {
          this.checkSocialLogin(res['code'], 'APPLE')
        }
        this.returnUrl.set(res['returnUrl']);

      })
    }
  }

  checkSocialLogin(code: string, source: string) {
    this.http.post('user/check-social-login', {provider: source, 'authToken': code}).subscribe((res: any) => {
      this.authService.saveUser(res);
      this.eventBus.cast(LOGIN, null);
      this.openTargetPage();
    })
  }

  signInWithApple() {
    const CLIENT_ID = "com.primeasiatv.laavan.login"
    const REDIRECT_API_URL = "https://apnerishte.com/callbacks/sign_in_with_apple"
    window.location.href = 'https://appleid.apple.com/auth/authorize?' +
      new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_API_URL,
        response_type: 'code',
      }).toString();
  };


  validateEmail($event: SubmitEvent) {
    $event.preventDefault();
    this.http.post('email/verify', this.loginModel()).subscribe({
      next: (res) => {
        this.loginModel.set({...this.loginModel(), confirm_password: ''})
        this.showRegister.set(true);
      },
      error: (err) => {
        if (err.status == 403) {
          this.showPassword.set(true);
        }
      }
    })
  }

  register(e: SubmitEvent) {
    e.preventDefault();
    this.http.post('user/register', this.loginModel()).subscribe((res: any) => {
      this.authService.saveUser(res);
      //  window.location.reload();
      setTimeout(() => {
        this.eventBus.cast(LOGIN);
      })
    })
  }

  openPasswordReset() {
    this.resetEmail.set(this.loginModel().email);
    this.resetError.set('');
    this.resetStep.set(1);
  }

  closePasswordReset() {
    this.resetStep.set(0);
    this.resetOtp.set('');
    this.resetToken.set('');
    this.newPassword.set('');
    this.confirmNewPassword.set('');
    this.resetError.set('');
  }

  requestPasswordReset(event: Event) {
    event.preventDefault();
    this.runResetRequest('user/reset-password', {email: this.resetEmail()}, () => this.resetStep.set(2));
  }

  verifyPasswordResetCode(event: Event) {
    event.preventDefault();
    this.runResetRequest('user/check-password-reset-code', {
      email: this.resetEmail(),
      otp: this.resetOtp(),
    }, (response: any) => {
      this.resetToken.set(response.token);
      this.resetStep.set(3);
    });
  }

  setNewPassword(event: Event) {
    event.preventDefault();
    if (this.newPassword().length < 8) {
      this.resetError.set('Password must be at least 8 characters.');
      return;
    }
    if (this.newPassword() !== this.confirmNewPassword()) {
      this.resetError.set('Passwords do not match.');
      return;
    }
    this.runResetRequest('user/set-new-password', {
      token: this.resetToken(),
      password: this.newPassword(),
      password_confirmation: this.confirmNewPassword(),
    }, () => this.resetStep.set(0));
  }

  private runResetRequest(url: string, body: object, onSuccess: (response: any) => void) {
    this.resetLoading.set(true);
    this.resetError.set('');
    this.http.post(url, body).subscribe({
      next: onSuccess,
      error: (error: HttpErrorResponse) => {
        this.resetError.set(error.error?.message || 'Unable to complete the request. Please try again.');
        this.resetLoading.set(false);
      },
      complete: () => this.resetLoading.set(false),
    });
  }

  ngOnInit(): void {
    if (this.isBrowser()) {
      if (window.google) {
        this.initializeGoogleSignIn();
      } else {
        // You might need a more robust loading check if the script loads slowly
        window.onload = () => this.initializeGoogleSignIn();
      }
    }
  }

  initializeGoogleSignIn(): void {
    window.google.accounts.id.initialize({
      use_fedcm_for_prompt: false,
      client_id: '502306360832-rnm80gcqfc2tl6a7jm83augj3uic9r1h.apps.googleusercontent.com', // Replace with your actual Client ID
      callback: (response: any) => this.handleCredentialResponse(response),
      auto_select: false,
      itp_support: false
    });
  }

  // This function is called by the Google SDK outside of the Angular zone
  handleCredentialResponse(res: any): void {
    this.checkSocialLogin(res.credential, 'GOOGLE')
  }

  createFakeGoogleWrapper() {
    const googleLoginWrapper = document.createElement('div');
    googleLoginWrapper.style.display = 'none'; // Hide the container
    document.body.appendChild(googleLoginWrapper);

    // Render the standard Google button inside the hidden container
    window.google.accounts.id.renderButton(googleLoginWrapper, {
      type: 'icon', // Use 'icon' for a minimal button
      width: '200',
    });

    // Find the actual button element inside the hidden container
    const googleLoginWrapperButton = googleLoginWrapper.querySelector(
      'div[role=button]'
    ) as HTMLElement;

    // Return an object that allows programmatic clicking
    return {
      click: () => {
        googleLoginWrapperButton?.click();
      },
    };
  }

  protected tryLogin() {
    const googleWrapper = this.createFakeGoogleWrapper();
    googleWrapper.click(); // Trigger the hidden button's click event
  }
}
